import { EntryNode } from '../../../types/definition';
import { SchemaBuilder } from '../../schema-builder/SchemaBuilder';
import { InputMappingEditor } from './InputMappingEditor';

interface EntryNodePropertiesProps {
  node: EntryNode;
  onChange: (updates: Partial<EntryNode>) => void;
  /**
   * Inspect, do not edit (task 881). Every control renders the same, disabled,
   * with no change handler attached — so what a reader sees is what an editor
   * edits, and nothing about reading it can write.
   */
  readOnly?: boolean;
}

export function EntryNodeProperties({ node, onChange, readOnly = false }: EntryNodePropertiesProps) {
  return (
    <div className="space-y-4" aria-readonly={readOnly || undefined}>
      {/* Node Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          value={node.name || ''}
          onChange={readOnly ? undefined : (e) => onChange({ name: e.target.value })}
          disabled={readOnly}
          placeholder="Entry"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-green-500 focus:ring-green-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
      </div>

      {/* Entry Point Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Entry Point
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Marks this as entry point (re-executes on continuation)
          </p>
        </div>
        <button
          type="button"
          onClick={readOnly ? undefined : () => onChange({ entry: !node.entry })}
          disabled={readOnly}
          className={`
            relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
            ${node.entry ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'}
          `}
        >
          <span
            className={`
              pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
              transition duration-200 ease-in-out
              ${node.entry ? 'translate-x-5' : 'translate-x-0'}
            `}
          />
        </button>
      </div>

      {/* Prompt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Prompt (optional)
        </label>
        <textarea
          value={node.prompt || ''}
          onChange={readOnly ? undefined : (e) => onChange({ prompt: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="Message to display when requesting input"
          rows={3}
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm
                     focus:border-green-500 focus:ring-green-500
                     dark:bg-gray-700 dark:text-white text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Optional message shown when requesting user input
        </p>
      </div>

      {/* Input Schema */}
      <div>
        <SchemaBuilder
          label="Input Schema"
          value={node.input ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ input: schema ?? undefined })}
        />
      </div>

      {/* Input Mapping */}
      <div>
        <InputMappingEditor
          value={node.inputMapping}
          readOnly={readOnly}
          onChange={(mapping) => onChange({ inputMapping: mapping })}
          schema={node.input}
        />
      </div>

      {/* Output Schema */}
      <div>
        <SchemaBuilder
          label="Output Schema"
          value={node.output ?? null}
          readOnly={readOnly}
          onChange={(schema) => onChange({ output: schema ?? undefined })}
        />
      </div>
    </div>
  );
}
